import { Encoding } from '@mode-vegas/vegas';
import { ChartFacade } from '@mode/charts/contract';
import { ChartDesigner, ChartState } from '@mode/legacy/types-app-state';
import { ChartDataTypes, ChartTypes, FlamingoTypes, InterActions, ReportTypes } from '@mode/shared/contract-common';
import { getCurrentUsername } from '@mode/shared/util-js';
import { FLAMINGO_RESERVED_REFS } from '@mode/shared/util-js';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { ToastService } from '@mode/shared/util-toast';
import { DataViewFacade, FlamingoFacade } from '@mode/switch-views/contract';
import type * as ngRouter from '@uirouter/angularjs';
import * as angular from 'angular';
import { Observable } from 'rxjs';
import { combineLatest, filter, map, startWith, takeUntil } from 'rxjs/operators';
import { ActiveExplorationActionType } from '~/actions/exploration/active-exploration.actions';
import { MixableBaseController } from '~/common/component';
import { ChartExportHelper } from '~/lib/chart-export-helper';
import { makeExplorationObservables } from '~/observables/exploration.observables';
import { getValue } from '~/rxjs-operators/getValue';
import { ChartDesignerSelectors } from '~/selectors/designer.selectors';
import { ExplorationVizService } from '~/services/exploration/exploration-viz.service';
import { FlamingoService } from '~/services/flamingo-service';
import { ReportApiService } from '~/services/report-api-service';
import { AppStore } from '~/types/main.types';
import { ExplorationObservables } from '~/types/viz-designer.types';
import { ReportViewDataService } from '../../services';

class ExplorationDesignerController extends MixableBaseController {
  static $inject = [
    'RxStoreService',
    'FlamingoService',
    'ToastService',
    'ExplorationVizService',
    'ReportViewDataService',
    'ReportApiService',
    'DataViewFacade',
    'ChartFacade',
    'FlamingoFacade',
    '$stateParams',
    '$window',
    '$state',
    'AnalyticsService',
    'FeatureFlagsFacade',
  ];

  // Observables
  private observables: ExplorationObservables;
  private name$: Observable<string>;
  private showChartPane$: Observable<boolean>;
  private chartPaneView$: Observable<ChartDesigner.PaneView>;
  private showDataPane$: Observable<boolean>;
  private dataPaneView$: Observable<ChartDesigner.PaneView>;
  private title$: Observable<string>;
  private chartType$: Observable<ChartTypes.ChartType>;
  private queryToken$: Observable<string>;
  private encoding$: Observable<Encoding>;
  private format$: Observable<ChartTypes.ChartFormat>;
  private fieldFormats$: Observable<ChartTypes.FieldFormatMap>;
  private chartState$: Observable<ChartState>;
  private colorPaletteToken$: Observable<string>;
  private chartData$: Observable<ChartDataTypes.Dataset>;
  private chartPaneHeight$: Observable<number>;
  private dataPaneHeight$: Observable<number>;
  private activeTableColumn$: Observable<string>;
  private explorationQueryExists$: Observable<boolean>;
  private isViewLoaded$: Observable<boolean>;
  private datasetForCopy$: Observable<string>;
  private usedParameters$: Observable<ReportTypes.ReportRunFormField[]>;
  private model$: Observable<FlamingoTypes.ModeModel>;

  private showResults$: Observable<boolean>;
  private planLoading$: Observable<boolean>;
  private invalid$: Observable<boolean>;
  private unappliedChangesCount$: Observable<number>;

  private isChart$: Observable<boolean>;
  private isTable$: Observable<boolean>;
  private isBigValue$: Observable<boolean>;

  // Local state
  private readOnly: boolean;
  private inputName: string;
  private sidebarOpen = true;
  private chartData: ChartDataTypes.Dataset;
  private objectContext = ChartTypes.ObjectType.Exploration;
  private chartType: ChartTypes.ChartType;

  constructor(
    private rxStore: AppStore,
    private flamingoService: FlamingoService, // for side effects
    private alertService: ToastService,
    private explorationVizService: ExplorationVizService,
    private reportViewDataService: ReportViewDataService,
    private reportApiService: ReportApiService,
    private dataViewFacade: DataViewFacade, // @TODO: (@anthonysimone) [GF] - temporary until active-exploration is migrated to angular and we implement an active-exploration facade that compiles the separate apis
    private chartFacade: ChartFacade,
    private flamingoFacade: FlamingoFacade,
    private $stateParams,
    private $window: angular.IWindowService,
    private $state: ngRouter.StateService,
    private analyticsService: AnalyticsService
  ) {
    super();

    this.readOnly = false;
  }

  $onInit() {
    this.observables = makeExplorationObservables(
      this.rxStore,
      this.dataViewFacade,
      this.chartFacade,
      this.flamingoFacade
    );

    this.name$ = this.observables.name$;
    this.title$ = this.observables.title$;
    this.chartType$ = this.observables.chartType$;
    this.queryToken$ = this.observables.queryToken$;
    this.encoding$ = this.observables.encoding$;
    this.format$ = this.observables.format$;
    this.fieldFormats$ = this.observables.fieldFormats$;
    this.chartState$ = this.observables.chartState$;
    this.colorPaletteToken$ = this.observables.colorPaletteToken$;
    this.chartData$ = this.observables.chartData$;
    this.planLoading$ = this.observables.planLoading$;
    this.explorationQueryExists$ = this.observables.explorationQueryExists$;
    this.isViewLoaded$ = this.observables.isViewLoaded$;
    this.datasetForCopy$ = this.observables.datasetForCopy$;
    this.unappliedChangesCount$ = this.observables.unappliedChangesCount$;
    this.usedParameters$ = this.observables.usedParameters$;
    this.model$ = this.observables.model$;

    // Derived observables
    this.isChart$ = this.observables.chartType$.pipe(map((type) => type !== 'table' && type !== 'bigValue'));
    this.isTable$ = this.observables.chartType$.pipe(map((type) => type === 'table'));
    this.isBigValue$ = this.observables.chartType$.pipe(map((type) => type === 'bigValue'));
    this.observables.chartType$.pipe(takeUntil(this.destroyed$)).subscribe((type) => (this.chartType = type));

    // This is `vizDesigner` feature state. Observables are created here for the time being.
    const designer$ = this.rxStore.select(ChartDesignerSelectors.getPanes).pipe(filter((d) => d != null));
    this.chartPaneView$ = designer$.pipe(map((d) => d.chartPane.view));
    this.dataPaneView$ = designer$.pipe(map((d) => d.dataPane.view));
    this.showChartPane$ = this.chartPaneView$.pipe(
      map((view) => view === ChartDesigner.PaneView.Regular || view === ChartDesigner.PaneView.Maximized)
    );
    this.showDataPane$ = this.dataPaneView$.pipe(
      map((view) => view === ChartDesigner.PaneView.Regular || view === ChartDesigner.PaneView.Maximized)
    );
    this.chartPaneHeight$ = designer$.pipe(map((d) => d.chartPane.height));
    this.dataPaneHeight$ = designer$.pipe(map((d) => d.dataPane.height));
    this.activeTableColumn$ = this.rxStore.select(ChartDesignerSelectors.getActiveTableColumn);

    // Build 2 designer only observables
    this.showResults$ = this.observables.canRender$.pipe(
      combineLatest(this.observables.planSucceeded$.pipe(startWith(false))),
      map(([canRender, success]) => canRender && success)
    );

    this.invalid$ = this.observables.canRender$.pipe(
      combineLatest(this.observables.planFailed$.pipe(startWith(false))),
      map(([canRender, failed]) => !canRender || failed)
    );
    // End observable init

    // Redirect user to focus if they are not the owner
    const currentUser = getCurrentUsername(this.$window.document);
    getValue(this.observables.explorationOwnerUserName$).then((explorationOwner) => {
      if (explorationOwner !== currentUser) {
        this.$state.go('explorationShow.focus', { explorationId: this.$stateParams.explorationId });
      }
    });

    // Report API Service embed handlers - Update on chart interactions (granularity, view data, etc)
    this.reportApiService
      .makeObservable(this.observables.token$)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ action, chartToken }) => {
        if (action.type === InterActions.Type.ContextClick) {
          switch (action.item.type) {
            case InterActions.ContextItemType.ViewData:
              this.rxStore.dispatch({
                type: ActiveExplorationActionType.ViewChartData,
                target: action.item.target,
                chartToken: FLAMINGO_RESERVED_REFS.Exploration,
              });
              break;
          }
        }
      });
  }

  toggleSidebar() {
    this.sidebarOpen = !this.sidebarOpen;
  }

  minimizeChartPane() {
    this.explorationVizService.minimizeChartPane();
  }

  maximizeChartPane() {
    this.explorationVizService.maximizeChartPane();
  }

  maximizeDataPane() {
    this.explorationVizService.maximizeDataPane();
  }

  minimizeDataPane() {
    this.explorationVizService.minimizeDataPane();
  }

  /**
   * Copy success.
   */
  copySuccess() {
    this.alertService.success('Copied to clipboard');
  }

  export(title: string, chartData: ChartDataTypes.Dataset) {
    if (chartData) {
      this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
        location: AnalyticsEvent.SubContext.Exploration,
        action: AnalyticsEvent.Interaction.DownloadCsv,
        resource: AnalyticsEvent.Context.Visualization,
        chartType: this.chartType,
      });

      ChartExportHelper.saveAs(ChartExportHelper.makeCsv(chartData), title);
    }
  }
}

export const ID = 'explorationDesigner';

export const ExplorationDesignerComponent: angular.IComponentOptions = {
  template: `
    <div class="exploration-designer">
      <div ng-if="(ctrl.explorationQueryExists$ | async:this) === true && (ctrl.isViewLoaded$ | async:this) === true" class="exploration-designer-body">
      <parameters-info
        ng-if="(ctrl.usedParameters$ | async:this).length > 0"
        parameters="ctrl.usedParameters$ | async:this"
        report-url="ctrl.$state.href('reportShow.noRuns.dashboard', { account: ctrl.$state.params.account, reportId: ctrl.$state.params.reportId, });"
      ></parameters-info>
      <viz-toolbar
          observables="ctrl.observables"
          read-only="ctrl.readOnly"
          model="ctrl.model$ | async:this"
          view-name="ctrl.queryToken$ | async:this"
          encoding="ctrl.encoding$ | async:this"
          format="ctrl.format$ | async:this"
          field-formats="ctrl.fieldFormats$ | async:this"
          selected-chart="ctrl.chartState$ | async:this"
          chart-service="ctrl.explorationVizService"
          on-open-sidebar="ctrl.onOpenSidebar()"
          toggle-sidebar="ctrl.toggleSidebar()"
          sidebar-open="ctrl.sidebarOpen"
          active-table-column="ctrl.activeTableColumn$ | async:this"
          object-context="ctrl.objectContext"
          viz-token="'${FLAMINGO_RESERVED_REFS.Exploration}'"
        ></viz-toolbar>
        <div class="vegas-sidebar" ng-show="ctrl.sidebarOpen">
          <viz-settings
            observables="ctrl.observables"
            chart-service="ctrl.explorationVizService"
            read-only="ctrl.readOnly"
            object-context="ctrl.objectContext"
            used-parameters="ctrl.observables.usedParameters$ | async:this"
          ></viz-settings>
        </div>
        <div class="designer-container">
          <div class="chart-designer" ng-class="{ 'with-sidebar': ctrl.sidebarOpen }" >
            <tiled-panes
              ng-if="!(ctrl.isTable$ | async:this)"
              vertical="true"
              adjustable="false"
              show-primary="ctrl.showChartPane$ | async:this"
              hide-primary-header="true"
              show-secondary="ctrl.showDataPane$ | async:this"
              secondary-collapsed-title="Chart Data"
              primary-height="ctrl.chartPaneHeight$ | async:this"
              secondary-height="ctrl.dataPaneHeight$ | async:this"
              on-primary-maximized="ctrl.maximizeChartPane()"
              on-primary-minimized="ctrl.minimizeChartPane()"
              on-secondary-maximized="ctrl.maximizeDataPane()"
              on-secondary-minimized="ctrl.minimizeDataPane()">

              <primary-header></primary-header>

              <primary-pane>
                <designer-renderer
                  ng-if="ctrl.isChart$ | async:this"
                  read-only="ctrl.readOnly"
                  observables="ctrl.observables"
                  chart-service="ctrl.explorationVizService"
                  is-view-page="false"
                  object-context="ctrl.objectContext"
                ></designer-renderer>
                <big-value-designer-renderer
                  ng-if="ctrl.isBigValue$ | async:this"
                  read-only="ctrl.readOnly"
                  observables="ctrl.observables"
                  chart-service="ctrl.explorationVizService"
                  is-view-page="false"
                  object-context="ctrl.objectContext"
                ></big-value-designer-renderer>
              </primary-pane>

              <secondary-header>
                <div class="results-button-container">
                  <capra-button
                    ng-if="ctrl.showResults$ | async:this"
                    text="Download"
                    variant="secondary"
                    color="neutral"
                    size="small"
                    (clicked)="ctrl.export(ctrl.chartTitle$ | async:this, ctrl.chartData$ | async:this)"
                    [disabled]="ctrl.unappliedChangesCount$ | async:this"
                  >
                  </capra-button>
                  <mode-clipboard-compat
                    tooltip-content="Copy results to clipboard"
                    [disabled]="!(ctrl.showResults$ | async:this) || (ctrl.unappliedChangesCount$ | async:this)"
                    [clipboard-text]="ctrl.datasetForCopy$ | async:this"
                    tag="capra-button"
                    text="Copy"
                  >
                  </mode-clipboard-compat>
                </div>
              </secondary-header>

              <secondary-pane>
                <designer-empty-table
                  ng-if="(!(ctrl.chartData$ | async: this) || (ctrl.unappliedChangesCount$ | async:this)) && (ctrl.showDataPane$ | async:this)"
                  pane-view="ctrl.dataPaneView$ | async:this"
                  chart-type="ctrl.chartType$ | async:this">
                </designer-empty-table>
                <div class="view-data-modal-table-container" ng-if="(ctrl.chartData$ | async:this) && !(ctrl.unappliedChangesCount$ | async:this)">
                  <pivot-results-data-grid
                    id="view-data-grid"
                    ng-if="(ctrl.showResults$ | async:this) && !(ctrl.unappliedChangesCount$ | async:this)"
                    dataset="ctrl.chartData$ | async:this">
                  </pivot-results-data-grid>
                  <mode-table-loading
                    ng-if="(ctrl.planLoading$ | async:this) || (ctrl.invalid$ | async:this)" [with-animation]="ctrl.planLoading$ | async:this"
                  ></mode-table-loading>
                </div>
              </secondary-pane>
            </tiled-panes>
            <designer-renderer
              ng-if="ctrl.isTable$ | async:this"
              read-only="ctrl.readOnly"
              observables="ctrl.observables"
              chart-service="ctrl.explorationVizService"
              is-view-page="false"
              object-context="ctrl.objectContext"
            ></designer-renderer>
          </div>
        </div>
        <calc-editor-modal is-exploration="true"></calc-editor-modal>
      </div>
      <div
        ng-if="(ctrl.explorationQueryExists$ | async:this) === false"
        class="exploration-error-container"
      >
        <mode-simple-error legacy-icon="icon-broken-viz">
          Sorry, this exploration can't be displayed. The underlying dataset has been deleted.
        </mode-simple-error>
      </div>

      <mode-explorations-file-exports></mode-explorations-file-exports>
    </div>
  `,
  controller: ExplorationDesignerController,
  controllerAs: 'ctrl',
};
